import Theme from '../theme';
import { colors } from '../constants/colors';
import { SHADOW_S, MODAL_SHADOW, MODAL_OVERLAY_BG } from '../shadows';
import { sharedTypography } from '../shared/typography';

export const lightTheme = new Theme({
    components: {
        callout_banner: {
            background: colors.Neutral2,
        },
        lyt_map_info_card: {
            background: colors.Neutral0,
            border: colors.Neutral3,
        },
        lyt_input: {
            background: {
                default: colors.Neutral0,
                hover: colors.Neutral1,
                focus: colors.Neutral0,
                error: colors.Neutral0,
            },
            border: {
                default: colors.Neutral4,
                focus: colors.Blue4,
                hover: colors.Neutral4,
                error: colors.Red3,
            }
        },
        lyt_textarea: {
            background: {
                default: colors.Neutral0,
                hover: '#F0F2F9', // Light hover background
                focus: colors.Neutral0,
                error: colors.Neutral0,
                disabled: colors.Neutral2,
            },
            border: {
                default: colors.Neutral4,
                focus: '#0063FF', // Light focused/selected border
                hover: colors.Neutral4,
                error: '#C8322A', // Light invalid border
                disabled: colors.Neutral3,
            },
            text: {
                default: colors.Neutral13,
                placeholder: colors.Neutral9, // color/text/subtext
                disabled: colors.Neutral8,
            }
        },
        lyt_checkbox: {
            border: {
                default: colors.Neutral7,
                hover: colors.Neutral7,
                pressed: colors.Neutral7,
                focus: colors.Blue5,
                error: colors.Red3,
            },
            background: {
                default: colors.Transparent,
                hover: colors.Neutral3,
                pressed: colors.Neutral4,
                focus: colors.Neutral0,
                error: colors.Neutral0,
            },
            checkmark: {
                default: colors.Blue4,
            },
            text: {
                selected: colors.Blue4,
            }
        },
        lyt_toggle: {
            background: {
                default: colors.Neutral4,
                hover: colors.Neutral5,
                toggled: colors.Green4,
                toggledHover: colors.Green5,
                toggledDisabled: colors.Green0,
                disabled: 'rgba(184, 185, 190, 0.30)',
            },
            thumb: {
                background: colors.Neutral0, // White thumb
            },
            label: {
                color: colors.Neutral13,
                disabled: colors.Neutral8,
            },
            sublabel: {
                color: colors.Neutral9,
            },
            border: {
                focus: colors.Blue4,
            }
        },
        lyt_radio: {
            border: {
                default: colors.Neutral6,
                focus: colors.Blue4,
                selected: colors.Blue4,
                disabled: colors.Neutral3,
                danger: colors.Red3,
            },
            background: {
                default: colors.Neutral0,
                hover: colors.Neutral1,
                active: colors.Blue1,
                selected: colors.Neutral0,
                disabled: colors.Neutral1,
                danger: colors.Red3,
            },
            dot: {
                selected: colors.Neutral0,
                disabled: colors.Neutral5,
                danger: colors.Neutral0,
            },
            label: {
                default: colors.Neutral13,
                disabled: colors.Neutral8,
            }
        },
        lyt_dropdown: {
            content: {
                background: colors.Neutral0,
                boxshadow: SHADOW_S,
                border: colors.Neutral3,
            }
        },
        lyt_tag: {
            background: {
                default: colors.Neutral2,
                hover: colors.Neutral3,
                pressed: colors.Neutral4,
                focus: colors.Neutral2,
            },
            text: {
                default: colors.Neutral11,
            },
            border: {
                focus: colors.Blue4,
            }
        },
        lyt_removeable_tag: {
            background: {
                default: colors.Neutral2,
                hover: colors.Red0,
                pressed: colors.Red1,
                focus: colors.Neutral2,
            },
            text: {
                default: colors.Neutral11,
            },
            border: {
                focus: colors.Blue4,
            }
        },
        lyt_badge: {
            default: {
                background: colors.Neutral2,
                border: colors.Transparent,
                text: colors.Neutral10,
            },
            'inverted-default': {
                background: colors.Neutral0,
                border: colors.Neutral3,
                text: colors.Neutral10,
            },
            primary: {
                background: colors.Blue4,
                border: colors.Transparent,
                text: colors.Neutral1,
            },
            'inverted-primary': {
                background: colors.Neutral0,
                border: colors.Blue4,
                text: colors.Blue4,
            },
            success: {
                background: colors.Green0,
                border: colors.Transparent,
                text: colors.Green7,
            },
            warning: {
                background: colors.Yellow0,
                border: colors.Transparent,
                text: colors.Yellow7,
            },
            important: {
                background: colors.Red0,
                border: colors.Transparent,
                text: colors.Red5,
            },
        },
        lyt_form: {
            background: colors.Neutral1,
            border: colors.Neutral4,
            shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            title: {
                color: colors.Neutral13,
            },
            subtitle: {
                color: colors.Neutral8,
            },
        },
        lyt_menu_item: {
            color: {
                default: colors.Neutral12,
                selected: colors.Blue4,
                disabled: colors.Neutral5,
                danger: colors.Red5,
            },
            icon: {
                default: colors.Neutral10,
                selected: colors.Blue4,
                disabled: colors.Neutral5,
                danger: colors.Red5,
            },
            background: {
                default: colors.Neutral0,
                hover: colors.Neutral3,
                pressed: colors.Neutral4,
                focus: colors.Neutral0,
                selected: colors.Blue0,
                selectedHover: colors.Blue1,
                selectedPressed: colors.Blue2,
                selectedFocus: colors.Blue0,
                disabled: colors.Neutral2,
            },
            border: {
                selected: colors.Blue4,
            },
            outline: {
                focus: colors.Blue4,
            }
        },
        lyt_button: {
            disabled_background: colors.Neutral5,
            primary: {
                background: {
                    default: colors.Blue4,
                    hover: colors.Blue5,
                    pressed: colors.Blue6,
                    focus: colors.Blue4,
                    disabled: colors.Neutral5,
                    selected: colors.Blue0,
                },
                border: {
                    focus: colors.Blue4,
                },
                text: {
                    default: colors.Neutral1,
                    hover: colors.Neutral1,
                    pressed: colors.Neutral1,
                    focus: colors.Neutral1,
                    disabled: colors.Neutral5,
                    selected: colors.Blue4,
                }
            },
            secondary: {
                background: {
                    default: colors.Neutral2,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral4,
                    focus: colors.Neutral2,
                    disabled: colors.Neutral5,
                    selected: colors.Blue0,
                },
                border: {
                    focus: colors.Blue3,
                },
                text: {
                    default: colors.Neutral10,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral10,
                    focus: colors.Neutral10,
                    disabled: colors.Neutral10,
                    selected: colors.Blue4,
                }
            },
            tertiary: {
                background: {
                    default: colors.Transparent,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral4,
                    focus: colors.Transparent,
                    disabled: colors.Transparent,
                    selected: colors.Blue0,
                },
                border: {
                    default: colors.Neutral3,
                    hover: colors.Neutral3,
                    focus: colors.Neutral3,
                    pressed: colors.Neutral3,
                    disabled: colors.Neutral5,
                    selected: colors.Blue0,
                    outline: colors.Blue3,
                },
                text: {
                    default: colors.Neutral10,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral10,
                    focus: colors.Neutral10,
                    disabled: colors.Neutral5,
                    selected: colors.Blue4,
                }
            },
            invisible: {
                background: {
                    default: colors.Transparent,
                    hover: colors.Neutral3,
                    pressed: colors.Neutral4,
                    focus: colors.Neutral2,
                    disabled: colors.Transparent,
                    selected: colors.Blue0,
                },
                border: {
                    focus: colors.Blue3,
                },
                text: {
                    default: colors.Neutral10,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral10,
                    focus: colors.Neutral10,
                    disabled: colors.Neutral10,
                    selected: colors.Blue4,
                }
            },
            toast: {
                background: {
                    default: '#F8F9FC59',
                    hover: '#F8F9FC80',
                    pressed: '#F8F9FC99',
                    focus: '#F8F9FC59',
                    disabled: colors.Neutral5,
                    selected: '#F8F9FC80',
                },
                border: {
                    outline: colors.Blue3,
                },
                text: {
                    default: colors.Neutral12,
                    hover: colors.Neutral12,
                    pressed: colors.Neutral12,
                    focus: colors.Neutral12,
                    disabled: colors.Neutral8,
                    selected: colors.Neutral12,
                }
            },
            toast_close: {
                background: {
                    default: colors.Transparent,
                    hover: colors.Transparent,
                    pressed: colors.Transparent,
                    focus: colors.Transparent,
                    disabled: colors.Transparent,
                    selected: colors.Transparent,
                },
                border: {
                    focus: colors.Blue3,
                },
                text: {
                    default: colors.Neutral10,
                    hover: colors.Neutral10,
                    pressed: colors.Neutral10,
                    focus: colors.Neutral10,
                    disabled: colors.Neutral5,
                    selected: colors.Neutral10,
                }
            },
            danger: {
                background: {
                    default: colors.Red3,
                    hover: colors.Red4,
                    pressed: colors.Red5,
                    focus: colors.Red3,
                    disabled: colors.Neutral5,
                    selected: colors.Red0,
                },
                border: {
                    focus: colors.Red3,
                },
                text: {
                    default: colors.Neutral1,
                    hover: colors.Neutral1,
                    pressed: colors.Neutral1,
                    focus: colors.Neutral1,
                    disabled: colors.Neutral6,
                    selected: colors.Red3,
                }
            },
        },
        table: {
            row: {
                evenBackground: colors.Neutral0,
                oddBackground: colors.Neutral6,
                text: colors.Neutral13,
            },
            border: colors.Neutral3,
        },
        dynamicTable: {
            headerBackground: colors.Neutral0,
        },
        linkTab: {
            active: {
                background: colors.Blue4,
                text: colors.Blue4,
            },
            color: colors.Neutral13,
        },
        dropdown: {
            input: {
                background: colors.Neutral0,
                text: colors.Neutral13,
                placeholder: colors.Neutral8,
                border: {
                    default: colors.Neutral3,
                    focus: colors.Blue3,
                    hover: colors.Neutral4
                },
                icon: colors.Neutral8,
                clearButton: {
                    background: colors.Neutral11,
                    hover: colors.Neutral10
                }
            },
            list: {
                background: colors.Neutral0,
                border: colors.Neutral3,
                shadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                scrollbar: {
                    track: colors.Neutral2,
                    thumb: {
                        default: colors.Neutral4,
                        hover: colors.Neutral5,
                        active: colors.Neutral6
                    }
                },
                item: {
                    text: colors.Neutral13,
                    background: colors.Transparent,
                    hover: {
                        text: colors.Blue3,
                        background: colors.Blue0
                    },
                    active: {
                        text: colors.Blue3,
                        background: colors.Blue0
                    }
                },
                noResults: {
                    text: colors.Neutral8
                }
            },
            transition: {
                duration: '0.2s',
                timing: 'ease-in-out'
            }
        },
        sidebar: {
            section_title: {
                color: colors.Neutral11,
            },
            borderRight: colors.Neutral3,
            background: colors.Neutral0,
            logo: {
                primary: colors.Neutral13,
                secondary: colors.Blue3,
            },
            dropdown: {
                background: colors.Neutral2,
                text: colors.Neutral13,
                subtext: colors.Neutral8,
                hover: {
                    background: colors.Neutral3,
                    text: colors.Neutral13,
                    subtext: colors.Neutral9,
                    icon: {
                        color: colors.Blue3
                    }
                },
                active: {
                    background: colors.Blue0,
                    text: colors.Blue4,
                    icon: {
                        color: colors.Blue4
                    }
                },
                panel: {
                    background: colors.Neutral0,
                    shadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.Neutral3}`,
                    zIndex: 1050
                },
                option: {
                    text: colors.Neutral13,
                    hover: {
                        background: colors.Blue0,
                        text: colors.Blue3
                    },
                    selected: {
                        background: colors.Blue0,
                        text: colors.Blue3,
                        checkmark: colors.Blue3
                    }
                },
                icon: {
                    color: colors.Neutral10,
                    hover: colors.Blue3
                },
                transition: {
                    duration: '0.2s',
                    timing: 'ease-in-out'
                },
                collapsed: {
                    background: colors.Neutral2,
                    hover: {
                        background: colors.Neutral3,
                        icon: {
                            color: colors.Blue3
                        }
                    }
                }
            }
        },
        mapOptions: {
            background: colors.Neutral0,
            text: colors.Neutral13,
            border: colors.Neutral3,
            radio: {
                unchecked: colors.Neutral8,
                checked: colors.Blue3,
                hover: colors.Blue2
            },
            collapse: {
                background: colors.Neutral0,
                border: colors.Neutral3
            },
            agencySelector: {
                label: colors.Neutral13,
                input: {
                    background: colors.Neutral0,
                    text: colors.Neutral13,
                    border: colors.Neutral3,
                    placeholder: colors.Neutral8
                },
                autocomplete: {
                    background: colors.Neutral0,
                    hover: {
                        background: colors.Blue0,
                        text: colors.Blue3
                    },
                    border: colors.Neutral3,
                    shadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }
            },
            routeSelector: {
                background: colors.Neutral0,
                text: colors.Neutral13,
                border: colors.Neutral3,
                placeholder: colors.Neutral8,
                multiValue: {
                    background: colors.Blue0,
                    text: colors.Blue3,
                    removeButton: {
                        color: colors.Blue3,
                        hover: colors.Blue4
                    }
                },
                dropdown: {
                    background: colors.Neutral0,
                    hoverBackground: colors.Blue0,
                    hoverText: colors.Blue3,
                    shadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }
            }
        },
        divider: {
            background: colors.Neutral3, // Light gray divider for light theme
            opacity: 0.6,
        },
        performancePanel: {
            background: colors.Neutral0,
            text: colors.Neutral13,
            border: colors.Neutral3,
            table: {
                header: {
                    background: colors.Neutral2,
                    text: colors.Neutral13
                },
                cell: {
                    text: colors.Neutral13
                }
            },
            statusColors: {
                green: '#01AC01',
                blue: '#0063ff',
                red: '#E95063'
            },
            collapse: {
                background: colors.Neutral0,
                border: colors.Neutral3
            },
            transition: {
                duration: '0.2s',
                timing: 'ease-in-out'
            }
        },
        lyt_tabs: {
            text: {
                default: colors.Neutral10,
                hover: colors.Neutral9,
                selected: colors.Blue4,
                disabled: colors.Neutral6,
            },
            border: {
                default: colors.Neutral3,
                hover: colors.Neutral6,
                selected: colors.Blue4,
            },
            background: {
                default: colors.Transparent,
                hover: colors.Neutral1,
                focus: colors.Transparent,
            },
            outline: {
                focus: colors.Blue4,
            }
        },
        lyt_tooltip: {
            background: colors.Neutral10,
            text: colors.Neutral1,
        },
        lyt_modal: {
            overlay: {
                background: MODAL_OVERLAY_BG, // primary-blue/07 at 12% opacity
            },
            dialog: {
                background: colors.Neutral0,
                text: colors.Neutral13,
                border: colors.Neutral3,
                shadow: MODAL_SHADOW, // L variant — base + 0 40px 80px -16px at 24%
            },
            header: {
                background: colors.Transparent, // Inherit from dialog
                text: colors.Neutral13,
                borderBottom: colors.Neutral3, // Optional divider
            },
            body: {
                background: colors.Transparent, // Inherit from dialog
                text: colors.Neutral11,
            },
            footer: {
                background: colors.Transparent, // Inherit from dialog
                borderTop: colors.Neutral3, // Optional divider
            },
            closeButton: {
                background: colors.Transparent,
                text: colors.Neutral10,
                hover: {
                    background: colors.Neutral2,
                    text: colors.Neutral13,
                }
            }
        },
        lyt_toast: {
            success: {
                background: '#26E98D', // Green success background (unique toast green)
                text: colors.Neutral12,
                subtleText: colors.Neutral12,
                icon: '#002F1C', // Deep green icon for success
            },
            error: {
                background: colors.Red3,
                text: colors.Neutral1,
                subtleText: colors.Neutral1,
                icon: colors.Neutral1,
            },
            warning: {
                background: colors.Yellow3,
                text: colors.Neutral11,
                subtleText: colors.Neutral11,
                icon: colors.Yellow6,
            },
            info: {
                background: colors.Neutral2,
                text: colors.Neutral12,
                subtleText: colors.Neutral12,
                icon: colors.Neutral10,
            },
            normal: {
                background: colors.Neutral0,
                text: colors.Neutral12,
                subtleText: colors.Neutral12,
                border: colors.Neutral3,
                icon: colors.Neutral10,
            },
            shadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
        lyt_accordion: {
            header: {
                background: {
                    default: colors.Neutral2,
                    open: colors.Blue0,
                    hover: colors.Neutral3,
                },
                text: {
                    default: colors.Neutral10,
                    open: colors.Blue4,
                },
                icon: {
                    default: colors.Neutral10,
                    open: colors.Blue4,
                },
            },
            content: {
                background: colors.Neutral2,
                text: colors.Neutral13,
            },
        }
    },
    // Background colors - use these for consistent background styling across components
    background: {
        default: colors.Neutral0,
        subtle: colors.Neutral2,
        elevated: colors.Neutral1,
        invert: colors.Neutral12,
        overlay: 'rgba(31, 31, 31, 0.5)', // overlay background
    },
    // Typography text colors - use these for consistent text styling across components
    text: {
        default: colors.Neutral12,
        subtle: colors.Neutral10,
        invert: colors.Neutral1,
        selected: colors.Blue4,
        fixed: colors.Neutral12, 
        disabled: colors.Neutral5,
        warning: {
            subtle: colors.Yellow6,
            default: colors.Yellow5, // warning text
        },
        link: {
            default: colors.Blue4,
            hover: colors.Blue5,
            pressed: colors.Blue6,
        },
        danger: {
            default: colors.Red5, // danger/error text
        },
        valid: {
            default: colors.Green5, // success/valid text
        },
    },
    // Default icon color
    icon: {
        default: colors.Neutral10,
    },
    // Import shared typography configuration
    ...sharedTypography,
    // CSS Variables
    cssVariables: {
        '--color-text-subtle': colors.Neutral10,
        '--color-text-default': colors.Neutral11,
        '--color-text-subtext': colors.Neutral9, 
        '--color-text-value': colors.Neutral11,
        '--color-text-error': colors.Red3,
        '--color-text-warning': colors.Yellow5,
        '--elevation-surfaces-primary': colors.Neutral0,
    }
});